import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";

/**
 * Page /reports
 * - Liste les rapports déjà générés par l'utilisateur
 * - Recherche par titre, filtre par type, tri par date
 * - Actions : Ouvrir, Télécharger, Copier l'URL, Supprimer
 */
// Chemin dans le bucket "documents" — soit stocké directement (file_path),
// soit à extraire de l'ancienne URL publique (anciens rapports : le bucket
// "documents" est privé, donc cette URL publique renvoie "Bucket not found" ;
// le fichier existe toujours, il faut juste repasser par une signed URL).
const getStoragePath = (report) => {
  if (report.file_path) return report.file_path;
  const marker = "/object/public/documents/";
  const idx = report.file_url ? report.file_url.indexOf(marker) : -1;
  if (idx === -1) return null;
  return decodeURIComponent(report.file_url.slice(idx + marker.length));
};

// Régénère une signed URL depuis le chemin de stockage.
const getAccessUrl = async (report) => {
  const path = getStoragePath(report);
  if (path) {
    const { data, error } = await supabase.storage
      .from("documents")
      .createSignedUrl(path, 60 * 60 * 2); // 2h
    if (!error && data?.signedUrl) return data.signedUrl;
  }
  // Dernier recours (ne fonctionnera pas si le bucket est privé, mais on ne
  // veut jamais renvoyer "aucun accès" si une URL quelconque est présente).
  return report.file_url || null;
};

const ReportsList = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [types, setTypes] = useState([]);

  // UI state
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortDir, setSortDir] = useState("desc"); // desc par défaut (plus récent en premier)
  const [deletingId, setDeletingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const fetchReports = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);

    try {
      // Requête simple sans jointure (évite le 400 si la FK n'est pas exposée)
      const { data: simple, error } = await supabase
        .from("reports")
        .select("id,title,type,options,file_url,created_at,collection_id")
        .eq("client_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Enrichissement des titres de collection en 2e requête
      const ids = [...new Set((simple || []).map(r => r.collection_id).filter(Boolean))];
      let mapTitle = {};
      if (ids.length) {
        const { data: cols } = await supabase
          .from("collections")
          .select("id,title")
          .in("id", ids);
        if (cols) {
          mapTitle = cols.reduce((acc, c) => { acc[c.id] = c.title; return acc; }, {});
        }
      }

      setReports((simple || []).map(r => ({
        ...r,
        collections: r.collection_id ? { title: mapTitle[r.collection_id] || "—" } : null
      })));
    } catch (err) {
      console.error("Erreur chargement rapports:", err);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Types distincts pour le filtre
  useEffect(() => {
    const t = [...new Set((reports || []).map(r => r.type).filter(Boolean))];
    setTypes(t);
  }, [reports]);

  const filtered = useMemo(() => {
    let out = [...reports];

    if (q.trim()) {
      const needle = q.toLowerCase();
      out = out.filter(r =>
        (r.title || "").toLowerCase().includes(needle) ||
        (r.type || "").toLowerCase().includes(needle) ||
        (r.collections?.title || "").toLowerCase().includes(needle)
      );
    }

    if (typeFilter !== "all") {
      out = out.filter(r => r.type === typeFilter);
    }

    out.sort((a, b) => {
      const da = new Date(a.created_at).getTime();
      const db = new Date(b.created_at).getTime();
      return sortDir === "asc" ? da - db : db - da;
    });

    return out;
  }, [reports, q, typeFilter, sortDir]);

  const handleCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      window.alert("Lien copié dans le presse-papiers.");
    } catch {
      window.alert("Impossible de copier le lien.");
    }
  };

  // Supprime le fichier du bucket puis la ligne en base. Le storage est
  // best-effort : un fichier déjà absent ne doit pas empêcher de nettoyer la
  // ligne orpheline en base (sinon un rapport cassé redevient impossible à retirer).
  const deleteReportFile = async (report) => {
    const path = getStoragePath(report);
    if (!path) return;
    const { error } = await supabase.storage.from("documents").remove([path]);
    if (error) console.warn("[ReportsList] fichier bucket non supprimé:", path, error.message);
  };

  const handleDelete = async (id) => {
     const proceed =
   typeof window !== "undefined" &&
   window.confirm("Supprimer ce rapport ? L’action est irréversible.");
 if (!proceed) return;
    setDeletingId(id);
    try {
      const report = reports.find(r => r.id === id);
      if (report) await deleteReportFile(report);
      const { error } = await supabase.from("reports").delete().eq("id", id);
      if (error) throw error;
      setReports(prev => prev.filter(r => r.id !== id));
      setSelectedIds(prev => { const next = new Set(prev); next.delete(id); return next; });
    } catch (e) {
      console.error(e);
      window.alert("Erreur lors de la suppression.");
    } finally {
      setDeletingId(null);
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev =>
      prev.size === filtered.length ? new Set() : new Set(filtered.map(r => r.id))
    );
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const proceed =
      typeof window !== "undefined" &&
      window.confirm(`Supprimer ${selectedIds.size} rapport(s) ? L’action est irréversible.`);
    if (!proceed) return;

    setBulkDeleting(true);
    try {
      const toDelete = reports.filter(r => selectedIds.has(r.id));
      await Promise.all(toDelete.map(deleteReportFile));

      const { error } = await supabase.from("reports").delete().in("id", [...selectedIds]);
      if (error) throw error;

      setReports(prev => prev.filter(r => !selectedIds.has(r.id)));
      setSelectedIds(new Set());
    } catch (e) {
      console.error(e);
      window.alert("Erreur lors de la suppression groupée.");
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <h1 className="text-3xl font-bold">Mes rapports</h1>

        <div className="flex flex-wrap gap-3">
          <Link to="/collections-reports" className="btn-artsy btn-primary-artsy">
            + Créer un rapport
          </Link>
        </div>
      </div>

      {/* Outils de recherche/filtre */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher par titre, type, collection…"
            className="w-full border border-gray-300 rounded-md py-2 px-3"
          />

          <select
            className="border border-gray-300 rounded-md py-2 px-3"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">Tous les types</option>
            {types.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Tri par date :</span>
            <button
              onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}
              className="px-3 py-2 border rounded-md"
              title="Inverser l’ordre"
            >
              {sortDir === "asc" ? "⬆️ Ancien → Récent" : "⬇️ Récent → Ancien"}
            </button>
          </div>
        </div>

        {filtered.length > 0 && (
          <div className="mt-3 flex items-center gap-4 border-t pt-3">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedIds.size > 0 && selectedIds.size === filtered.length}
                onChange={toggleSelectAll}
              />
              Tout sélectionner
            </label>
            {selectedIds.size > 0 && (
              <>
                <span className="text-sm text-gray-500">{selectedIds.size} sélectionné(s)</span>
                <button
                  className="btn-artsy btn-danger-artsy ml-auto"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                >
                  {bulkDeleting ? "Suppression…" : `Supprimer la sélection (${selectedIds.size})`}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Liste */}
      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-lg text-gray-600 mb-4">Aucun rapport pour l’instant.</p>
          <Link to="/collections-reports" className="btn-artsy btn-primary-artsy">
            Créer mon premier rapport
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filtered.map((r) => {
            const created = new Date(r.created_at);
            const collectionTitle = r.collections?.title || (r.collection_id ? `#${r.collection_id}` : null);

            return (
              <div
                key={r.id}
                className="border border-gray-200 bg-white rounded-lg p-5 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1.5"
                        checked={selectedIds.has(r.id)}
                        onChange={() => toggleSelected(r.id)}
                        aria-label={`Sélectionner ${r.title || "ce rapport"}`}
                      />
                      <div>
                      <h3 className="text-xl font-semibold">{r.title || "Rapport"}</h3>
                      <div className="mt-1 text-sm text-gray-600">
                        <span className="inline-block px-2 py-0.5 rounded bg-gray-100 border mr-2">
                          {r.type}
                        </span>
                        <span>
                          Créé le {created.toLocaleDateString("fr-FR")} à{" "}
                          {created.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                      {collectionTitle && (
                        <div className="mt-1 text-sm text-gray-600">
                          Collection : <span className="font-medium">{collectionTitle}</span>
                        </div>
                      )}
                      </div>
                    </div>
                  </div>

                  {/* Aperçu des options (bref) */}
                  {r.options && (
                    <div className="mt-3 text-sm text-gray-500">
                      {r.options.includeThumbnails ? "🖼️ vignettes" : "🗂️ liste"}
                      {" • "}
                      {r.options.groupBy && r.options.groupBy !== "none"
                        ? `groupé par ${r.options.groupBy}`
                        : "non groupé"}
                      {" • "}
                      tri: {r.options.sortBy || "name"}
                    </div>
                  )}
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  {(r.file_url || r.file_path) ? (
                    <>
                      <button
                        className="btn-artsy btn-secondary-artsy"
                        title="Ouvrir dans un nouvel onglet"
                        onClick={async () => {
                          const url = await getAccessUrl(r);
                          if (url) window.open(url, "_blank");
                          else window.alert("Impossible d'accéder au fichier.");
                        }}
                      >
                        Ouvrir
                      </button>
                      <button
                        className="btn-artsy btn-secondary-artsy"
                        title="Télécharger le PDF"
                        onClick={async () => {
                          const url = await getAccessUrl(r);
                          if (!url) { window.alert("Impossible d'accéder au fichier."); return; }
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `${r.title || "rapport"}.pdf`;
                          a.click();
                        }}
                      >
                        Télécharger
                      </button>
                      <button
                        className="btn-artsy btn-secondary-artsy"
                        onClick={async () => {
                          const url = await getAccessUrl(r);
                          if (url) handleCopy(url);
                          else window.alert("Impossible d'accéder au fichier.");
                        }}
                      >
                        Copier le lien
                      </button>
                    </>
                  ) : (
                    <span className="text-sm text-red-600">Fichier introuvable</span>
                  )}

                  <button
                    className="btn-artsy btn-danger-artsy ml-auto"
                    onClick={() => handleDelete(r.id)}
                    disabled={deletingId === r.id}
                    title="Supprimer"
                  >
                    {deletingId === r.id ? "Suppression…" : "Supprimer"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReportsList;